import React, { useCallback, useEffect, useState } from 'react';
import * as R from 'ramda';
import AddRounded from '@mui/icons-material/AddRounded';
import { Box, Button, CircularProgress, Drawer } from '@mui/material';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { DrawerLayoutWrapper } from 'common';
import { bool, func, number, shape } from 'prop-types';
import { createSnack } from 'snackbar/snackbarSlice';
import { useDispatch } from 'react-redux';
import DialogExplicitConfirmation from 'common/dialog-explicit-confirmation/DialogExplicitConfirmation';
import { GT_ZERO, REQUIRED } from 'utils/helpers/validationMessages';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import DialogUnsavedChanges from '../components/DialogUnsavedChanges';
import {
  useCreateDistributionsMutation,
  useDeleteDistributionMutation,
  useGetInventoryOwnersQuery,
  useGetLocationsQuery,
  useGetRecipientsQuery,
  useGetShipmentDistributionsQuery,
  useSaveDistributionMutation,
  useViewShipmentQuery,
} from '../batchesEndpoints';
import {
  baseDistribution,
  emptyDistributionDetailsState,
} from '../distributions/create/initialState';
import EditViewDistribution from './EditViewDistribution';
import DataDisplayItem from '../components/DataDisplayItem';
import NoAssociatedCard from '../components/NoAssociatedCard';

export const DISTRIBUTIONS_DRAWER_MODE = { EDIT: true, VIEW: false };

const EditDistributionsDrawer = ({
  batchData,
  shipmentId,
  mode = DISTRIBUTIONS_DRAWER_MODE.VIEW,
  setSelectedShipment,
  onClose,
  refetchShipments,
}) => {
  const initialValidationState = {
    chainRecipients: '',
    locationName: '',
    volume: '',
  };

  const [initialDistributions, setInitialDistributions] = useState({
    ...emptyDistributionDetailsState,
    shipmentId,
  });
  const [distributions, setDistributions] = useState({
    ...emptyDistributionDetailsState,
    shipmentId,
  });
  const [isSavingDistributions, setIsSavingDistributions] = useState(false);
  const [confirmationDialog] = useState();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [unsavedDialog, setUnsavedDialog] = useState();
  const [availableVolumeExceeded, setAvailableVolumeExceeded] = useState(false);
  const [invalidDistribution, setInvalidDistribution] = useState(false);
  const [validationMessages, setValidationMessages] = useState([initialValidationState]);
  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false);
  const [deleteId, setDeleteId] = useState();

  const {
    data: shipmentDistributionsData = { distributions: [] },
    isFetching: isFetchingDistributions,
    isSuccess: isSuccessDistributions,
  } = useGetShipmentDistributionsQuery(
    {
      params: { shipmentId: parseInt(shipmentId) },
    },
    {
      skip: !shipmentId,
    }
  );

  const {
    data: shipmentData = { shipments: [] },
    isSuccess: isShipmentDataSuccess,
    refetch: refetchShipment,
  } = useViewShipmentQuery(
    {
      params: { shipmentId: parseInt(shipmentId) },
    },
    {
      skip: !shipmentId,
    }
  );

  useEffect(() => {
    if (!R.isNil(refetchShipment)) {
      refetchShipment();
    }
  }, [refetchShipment, shipmentId]);

  const [saveEditedDistribution] = useSaveDistributionMutation();
  const [deleteDistribution] = useDeleteDistributionMutation();

  const dispatch = useDispatch();

  const dataCompanyId = R.path(['shipper', 'dataCompanyId'], shipmentData);

  const isEditMode = mode === DISTRIBUTIONS_DRAWER_MODE.EDIT;

  const showSnack = ({ error, data: { message } = {} }) =>
    dispatch(
      createSnack(
        error
          ? {
              message: error.data.title,
              options: { variant: 'error' },
            }
          : { message }
      )
    );

  const hasChanges = () => {
    const newDistributions = R.filter(
      R.propSatisfies(R.isNil, 'distributionId'),
      distributions?.distributions
    );
    const initialDistributionsWithoutTempId = R.map(
      R.omit(['tempId']),
      initialDistributions?.distributions
    );
    const distributionsWithoutTempId = R.map(R.omit(['tempId']), distributions?.distributions);
    const hasC =
      !R.equals(initialDistributionsWithoutTempId, distributionsWithoutTempId) ||
      !R.equals(newDistributions, []);
    return hasC;
  };

  useEffect(() => {
    if (isSuccessDistributions) {
      setDistributions(R.assoc('distributions', shipmentDistributionsData.distributions));
      setInitialDistributions(R.assoc('distributions', shipmentDistributionsData.distributions));
      setValidationMessages(
        R.repeat(
          {
            chainRecipients: '',
            volume: '',
          },
          shipmentDistributionsData?.distributions?.length
        )
      );
    }
  }, [isSuccessDistributions, shipmentDistributionsData]);

  const inventoryOwnersParams = { dataCompanyId };

  const { data: inventoryOwnersData = [] } = useGetInventoryOwnersQuery(
    { params: inventoryOwnersParams },
    { skip: R.isNil(dataCompanyId) }
  );

  const {
    data: recipientsQuery = [],
    isFetching: isFetchingRecipients,
    isSuccess: isRecipientsSuccess,
  } = useGetRecipientsQuery(
    {
      params: {
        dataCompanyId,
      },
    },
    { skip: R.isNil(dataCompanyId) }
  );

  const handleInventoryOwnerChange = (id, index, value) => {
    setDistributions(
      R.assocPath(
        ['distributions', index, 'inventoryOwnerName'],
        R.prop('inventoryOwnerName', value)
      )
    );
    setValidationMessages(
      R.update(index, R.assoc('inventoryOwnerName', '', validationMessages[index]))
    );
  };

  const locationsParams = {
    bookingOriginId: R.prop('originId', shipmentData),
    dataCompanyId,
    productId: R.prop('productId', shipmentData),
  };

  // Fetch locations based on the booking origin and product ID
  const { data: locationsData = [] } = useGetLocationsQuery(
    { params: locationsParams },
    { skip: R.isNil(dataCompanyId) || R.isNil(locationsParams.bookingOriginId) }
  );

  const handleLocationChange = (id, index, value) => {
    setDistributions(
      R.assocPath(['distributions', index, 'locationName'], R.prop('locationName', value))
    );
    setDistributions(R.assocPath(['distributions', index, 'locationId'], R.prop('id', value)));
    setValidationMessages(R.update(index, R.assoc('locationId', '', validationMessages[index])));
  };

  const validateVolume = R.cond([
    [R.isEmpty(R.__), R.always(REQUIRED)],
    [R.gt(1), R.always(GT_ZERO)],
    [R.T, R.always('')],
  ]);

  const handleVolumeChange = (id, index, value) => {
    const sanitized = R.replace(/[^0-9]/g, '', value);
    setDistributions(R.assocPath(['distributions', index, 'volume'], sanitized));
    setValidationMessages(
      R.update(index, R.assoc('volume', validateVolume(sanitized), validationMessages[index]))
    );
  };

  const isVolumeExceeded = useCallback(() => {
    const totalVolume = R.pipe(
      R.map(v => {
        const parsed = R.prop('volume', v);
        return R.isNil(parsed) ? 0 : parseInt(parsed);
      }),
      R.reduce(R.add, 0)
    )(distributions?.distributions);

    return totalVolume > parseInt(shipmentData?.volume);
  }, [distributions, shipmentData]);

  useEffect(() => {
    setAvailableVolumeExceeded(isVolumeExceeded());
  }, [distributions, isVolumeExceeded]);

  const handleShipmentChainChange = (index, evt) => {
    const { checked } = evt.target;

    if (checked) {
      setDistributions(R.assocPath(['distributions', index, 'shipmentChain'], true));
    } else {
      setDistributions(R.assocPath(['distributions', index, 'shipmentChain'], false));
    }

    // reset chainRecipients when shipment chain required is toggled off
    if (R.not(checked)) {
      setDistributions(R.assocPath(['distributions', index, 'chainRecipients'], []));
      setValidationMessages(
        R.update(index, R.assoc('chainRecipients', '', validationMessages[index]))
      );
    }
  };

  const handleReconsignmentControlChange = (index, evt) => {
    const { checked } = evt.target;
    setDistributions(R.assocPath(['distributions', index, 'reconsignmentControl'], !!checked));
  };

  const handleDeleteDistribution = () => {
    deleteDistribution({ body: { distributionId: deleteId } })
      .then(res => {
        setConfirmationDialogOpen(false);
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
        }
      })
      .finally(() => {
        refetchShipment();
        setTimeout(() => {
          setDeleteId(null);
          setDeleteDialogOpened(false);
        }, 500);
      });
  };

  const renderDeleteDialog = (id, idx) => {
    if (R.isNil(id)) {
      setDistributions(
        R.assoc('distributions', R.remove(idx, 1, R.prop('distributions', distributions)))
      );
      setDeleteId(null);
      setDeleteDialogOpened(false);
      return;
    }
    setDeleteId(id);
    setDeleteDialogOpened(true);
  };

  const handleEditDistributionClose = () => {
    const leftButtonAction = () => {
      setSelectedShipment(null);
      setValidationMessages([initialValidationState]);
      setUnsavedDialog();
      refetchShipments();
    };

    hasChanges()
      ? setUnsavedDialog({
          leftButtonAction,
          rightButtonAction: () => setUnsavedDialog(),
        })
      : leftButtonAction();
  };

  const recipientToAffiliate = ({ recipientIdName: companyCustomerIdName }) => ({
    companyCustomerIdName,
    dataCompanyId: parseInt(dataCompanyId),
  });

  // eslint-disable-next-line no-unused-vars
  const [createNewDistributions, { data, isSuccess, isFetching, isError, error }] =
    useCreateDistributionsMutation();

  const extractFirstIntegersFromString = str => {
    if (!str) return null;
    const matches = str.match(/(\d+)/g);
    if (matches && matches.length > 0) {
      return parseInt(matches[0], 10).toString();
    }
    return null;
  };

  const saveNewDistributions = async () => {
    const newDistributions = R.filter(
      R.propSatisfies(R.isNil, 'distributionId'),
      distributions?.distributions
    );

    if (R.isEmpty(newDistributions)) return;

    const body = {
      distributions: newDistributions.map(dist => ({
        ...R.omit(['volumeDisplay', 'locationName', 'tempId', 'inventoryOwnerName'], dist),
        chainRecipients: dist.chainRecipients.map(recipientToAffiliate),
        inventoryOwner: {
          companyCustomerIdName: extractFirstIntegersFromString(R.prop('inventoryOwnerName', dist)),
          dataCompanyId: parseInt(dataCompanyId),
        },
        shipmentChain: !!dist.shipmentChain,
      })),
      shipmentId,
    };

    const result = await createNewDistributions({ body });
    showSnack(result);
  };

  const saveEditedDistributions = async () => {
    const distributionsSaveArr = [];
    const distributionsWithDistributionIds = R.filter(
      R.propSatisfies(R.is(Number), 'distributionId'),
      distributions?.distributions
    );
    const editedDistributions = R.differenceWith(
      R.equals,
      distributionsWithDistributionIds,
      initialDistributions?.distributions
    );
    if (R.isEmpty(editedDistributions)) return;

    editedDistributions.forEach(distribution => {
      const body = {
        chainRecipients: R.prop('chainRecipients', distribution).map(cr => ({
          companyCustomerIdName: cr.recipientIdName,
          dataCompanyId,
        })),
        distributionId: R.prop('distributionId', distribution),
        inventoryOwner: R.prop('inventoryOwnerName', distribution),
        locationId: locationsData.find(loc => loc.locationName === distribution.locationName)?.id,
        reconsignmentControl: R.prop('reconsignmentControl', distribution),
        shipmentChain: !!R.prop('shipmentChain', distribution),
        shipmentId,
        volume: R.prop('volume', distribution),
      };
      distributionsSaveArr.push(saveEditedDistribution({ body }));
    });

    const res = await Promise.all(distributionsSaveArr);
    const resdata = res.map(r => r.data);
    const successes = [];
    const errors = [];
    resdata.forEach(d => {
      if (d.error) {
        errors.push(d.error);
      } else {
        successes.push(d.message);
      }
    });

    if (successes.length > 0) {
      dispatch(
        createSnack({
          message: `Successfully saved ${successes.length} distribution${
            successes.length === 1 ? '' : 's'
          }.`,
        })
      );
    }

    if (errors.length > 0) {
      dispatch(
        createSnack({
          message: 'There was an error saving your distributions.',
          options: { variant: 'error' },
        })
      );
    }
  };

  const handleSaveSubmit = async () => {
    if (!hasChanges()) return;
    setIsSavingDistributions(true);
    await saveEditedDistributions();
    await saveNewDistributions();
    setIsSavingDistributions(false);
    refetchShipments();
    onClose();
  };

  const renderEditButtons = () => (
    <Box
      sx={{
        '> *': { flex: 1 },
        '> :nth-of-type(2)': { ml: 4 },
        display: 'flex',
        width: '100%',
      }}
    >
      <Button onClick={handleEditDistributionClose} variant="outlined">
        Back
      </Button>

      <Button
        disabled={!hasChanges() || availableVolumeExceeded || invalidDistribution}
        loading={isFetching || isSavingDistributions}
        loadingIndicator="Saving..."
        onClick={handleSaveSubmit}
        variant="contained"
      >
        Save
      </Button>
    </Box>
  );

  const renderViewButtons = () => (
    <Box
      sx={{
        '> *': { flex: 1 },
        '> :nth-of-type(2)': { ml: 4 },
        display: 'flex',
        width: '100%',
      }}
    />
  );
  const handleChainRecipientsChange = (index, value) => {
    setDistributions(state => {
      const newState = R.clone(state);
      newState.distributions[index].chainRecipients = value;
      return newState;
    });

    setValidationMessages(state => {
      const newState = R.clone(state);
      newState[index] = R.assoc(
        'chainRecipients',
        R.isEmpty(value) ? REQUIRED : '',
        newState[index]
      );
      return newState;
    });
  };

  const renderTags = (tags, getTagProps) => {
    return tags.map((item, index) => (
      <Chip
        label={item.inventoryOwnerName ?? item.recipientName}
        sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
        variant="outlined"
        {...getTagProps({ index })}
      />
    ));
  };

  useEffect(() => {
    const isNilOrZero = val => R.or(R.isNil(val), R.equals(0, val));
    const mandatoryPropsHaveValues = R.all(
      R.pipe(
        R.pick(['inventoryOwnerName', 'locationId', 'volume']),
        R.values,
        R.all(R.complement(isNilOrZero))
      ),
      distributions?.distributions ?? []
    );

    // Checks if any distribution has shipmentChain enabled but no recipients
    const hasInvalidShipmentChain = R.any(
      dist =>
        dist.shipmentChain && (R.isNil(dist.chainRecipients) || R.isEmpty(dist.chainRecipients)),
      distributions?.distributions ?? []
    );

    const allDistributionsAreValid = R.all(
      R.pipe(R.values, R.all(R.equals(''))),
      validationMessages
    );

    setInvalidDistribution(
      !mandatoryPropsHaveValues || !allDistributionsAreValid || hasInvalidShipmentChain
    );
  }, [validationMessages, distributions?.distributions]);

  const handleAddDistributionClick = () => {
    const newDist = R.assoc('tempId', Math.random(), baseDistribution);
    setDistributions(R.assoc('distributions', R.append(newDist, distributions.distributions)));
  };

  if (R.isNil(shipmentData?.originId)) return <></>;

  return (
    <Drawer
      anchor="right"
      onClose={handleEditDistributionClose}
      open={!!shipmentId}
      slotProps={{
        paper: { sx: { width: { lg: 380, xs: '100%' } } },
      }}
      variant="temporary"
    >
      <DialogUnsavedChanges open={!!unsavedDialog} {...unsavedDialog} />
      <DialogExplicitConfirmation
        confirmationValue=""
        dialogOpen={confirmationDialogOpen}
        handleCloseDialog={() => setConfirmationDialogOpen(false)}
        onConfirm={() => {
          setConfirmationDialogOpen(false);
          confirmationDialog.onConfirmAction();
        }}
        {...confirmationDialog}
      />
      <DrawerLayoutWrapper
        buttons={isEditMode ? renderEditButtons() : renderViewButtons()}
        onHeaderButtonClick={handleEditDistributionClose}
        title={isEditMode ? 'Edit Distribution(s)' : 'View Distribution(s)'}
      >
        <Box sx={{ pt: 3, px: { lg: 3, xs: 2 } }}>
          {isFetchingDistributions || isSavingDistributions ? (
            <Box textAlign="center" width="100%">
              <CircularProgress size={48} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                position: 'relative',
                rowGap: { lg: 3, xs: 2 },
              }}
            >
              <DataDisplayItem label="Shipment Id" value={shipmentId} />
              <DataDisplayItem
                label="Shipment Volume"
                value={formatNumberWithoutDecimals(R.prop('volume', shipmentData))}
              />
              <DataDisplayItem
                label="Total Distributed Volume"
                value={formatNumberWithoutDecimals(R.prop('totalDistributedVolume', shipmentData))}
              />
            </Box>
          )}
          {isShipmentDataSuccess &&
            (R.isEmpty(distributions?.distributions) ? (
              <>
                <Stack spacing={2} sx={{ py: { md: 3, xs: 2 } }}>
                  <NoAssociatedCard type="distribution" />
                </Stack>
              </>
            ) : (
              distributions?.distributions?.map((d, index) => (
                <EditViewDistribution
                  availableVolumeExceeded={availableVolumeExceeded}
                  batchData={batchData}
                  dataCompanyId={dataCompanyId}
                  distribution={d}
                  formatNumberWithoutDecimals={formatNumberWithoutDecimals}
                  handleChainRecipientsChange={handleChainRecipientsChange}
                  handleInventoryOwnerChange={handleInventoryOwnerChange}
                  handleLocationChange={handleLocationChange}
                  handleReconsignmentControlChange={handleReconsignmentControlChange}
                  handleShipmentChainChange={handleShipmentChainChange}
                  handleVolumeChange={handleVolumeChange}
                  index={index}
                  inventoryOwnersData={inventoryOwnersData}
                  isFetchingRecipients={isFetchingRecipients}
                  isNew={R.isNil(R.prop('distributionId', d))}
                  isRecipientsSuccess={isRecipientsSuccess}
                  key={
                    R.prop('distributionId', d) || `temp-${R.prop('tempId', d) || Math.random()}`
                  }
                  mode={mode}
                  recipientsQuery={recipientsQuery}
                  renderDeleteDialog={renderDeleteDialog}
                  renderTags={renderTags}
                  setValidationMessages={setValidationMessages}
                  shipmentData={shipmentData}
                  validationMessages={validationMessages}
                />
              ))
            ))}
          {isEditMode && (
            <>
              <Divider />
              <Button
                onClick={handleAddDistributionClick}
                startIcon={<AddRounded sx={{ color: 'secondary.main', mt: -0.5 }} />}
                sx={{ justifyContent: 'flex-start' }}
                variant="text"
              >
                New Distribution
              </Button>
            </>
          )}
        </Box>
        <DialogExplicitConfirmation
          confirmationMessage="If you still want to delete the distribution, enter the word below and click Confirm."
          confirmationValue="DELETE"
          dialogOpen={deleteDialogOpened}
          handleCloseDialog={() => {
            setDeleteDialogOpened(false);
          }}
          label='Type "DELETE"'
          onConfirm={handleDeleteDistribution}
          title="Are you sure you want to delete this distribution?"
          warningList={['This action cannot be undone.']}
        />
      </DrawerLayoutWrapper>
    </Drawer>
  );
};

EditDistributionsDrawer.propTypes = {
  batchData: shape({}).isRequired,
  mode: bool,
  onClose: func.isRequired,
  refetchShipments: func.isRequired,
  setSelectedShipment: func.isRequired,
  shipmentId: number,
};

EditDistributionsDrawer.defaultProps = {
  mode: DISTRIBUTIONS_DRAWER_MODE.VIEW,
  shipmentId: null,
};

export default EditDistributionsDrawer;
